if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form.section-filters-form");
      this.button = this.querySelector('button[type="submit"]');

      this.collectionSelect = this.querySelector("select.dropdown-collection");
      this.collectionInput = this.querySelector("input.input-collection");

      this.filterSelects = Array.from(this.querySelectorAll("select.dropdown-filter"));

      this.fitmentGidInput = this.querySelector("[data-fitment-keys-input]");
      this.fitmentIndex = this.loadFitmentIndex();

      const roles = this.detectRoles(this.filterSelects);
      this.selectMake   = roles.make   || this.filterSelects[0] || null;
      this.selectModel  = roles.model  || this.filterSelects[1] || null;
      this.selectYear   = roles.year   || this.filterSelects[2] || null;
      this.selectEngine = roles.engine || this.filterSelects[3] || null;

      this.setupEventListeners();

      this.resetAll();
      this.populateMakes();
      this.restoreFromURL();
      this.updateButtonAndAction();
    }

    setupEventListeners() {
      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        const url = this.buildFinalURL();
        if (url) window.location.href = url;
      });

      this.collectionSelect?.addEventListener("change", () => {
        this.updateButtonAndAction();
      });

      this.selectMake?.addEventListener("change",   () => this.onMakeChange());
      this.selectModel?.addEventListener("change",  () => this.onModelChange());
      this.selectYear?.addEventListener("change",   () => this.onYearChange());
      this.selectEngine?.addEventListener("change", () => this.onEngineChange());
    }

    loadFitmentIndex() {
      const el = this.querySelector("script[data-fitment-index]");
      if (!el) return [];
      try {
        const data = JSON.parse(el.textContent || "[]");
        if (!Array.isArray(data)) return [];
        return data
          .map((x) => ({
            make:   (x.make   ?? x.Make   ?? "").toString().trim(),
            model:  (x.model  ?? x.Model  ?? "").toString().trim(),
            year:   (x.year   ?? x.Year   ?? "").toString().trim(),
            engine: (x.engine ?? x.Engine ?? "").toString().trim(),
            key:    (x.key    ?? x.fitment_key ?? x.fitmentKey ?? "").toString().trim(),
            gid:    (x.gid    ?? x.id     ?? "").toString().trim(),
          }))
          .filter((x) => x.make && x.model && x.year && x.engine);
      } catch (e) {
        console.error("[Fitment] Invalid JSON in data-fitment-index", e);
        return [];
      }
    }

    detectRoles(selects) {
      const byLabel = {};
      for (const s of selects) {
        const label = (s.dataset.label || "").toLowerCase().trim();
        if (label) byLabel[label] = s;
      }
      const pick = (arr) => arr.map((k) => byLabel[k]).find(Boolean);
      return {
        make:   pick(["make", "brand", "vehicle make"]),
        model:  pick(["model", "vehicle model"]),
        year:   pick(["year", "vehicle year"]),
        engine: pick(["engine", "motor"]),
      };
    }

    getBaseCollectionURL() {
      const selected = this.collectionSelect?.value;
      const single   = this.collectionInput?.value;
      const fallback = window.routes?.all_products_url ?? "/collections/all";
      return selected || single || fallback;
    }

    normalizeSortUnique(values) {
      const set = new Set(values.filter(Boolean).map((v) => v.toString().trim()));
      return Array.from(set).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
      );
    }

    clearOptions(select) {
      if (!select) return;
      const keep = select.querySelector("option.default");
      select.innerHTML = "";
      if (keep) {
        select.appendChild(keep);
        keep.selected = true;
      }
    }

    setDisabled(select, disabled) {
      if (!select) return;
      select.disabled = disabled;
      const group = select.closest(".form-group");
      if (group) group.classList.toggle("disabled", disabled);
    }

    getValue(select) {
      if (!select) return "";
      return (select.value || "").toString().trim();
    }

    appendOptions(select, values) {
      if (!select) return;
      for (const v of values) {
        const opt = document.createElement("option");
        opt.value = v;
        opt.textContent = v;
        select.appendChild(opt);
      }
    }

    resetAll() {
      this.clearOptions(this.selectMake);
      this.clearOptions(this.selectModel);
      this.clearOptions(this.selectYear);
      this.clearOptions(this.selectEngine);

      this.setDisabled(this.selectMake,   false);
      this.setDisabled(this.selectModel,  true);
      this.setDisabled(this.selectYear,   true);
      this.setDisabled(this.selectEngine, true);

      if (this.fitmentGidInput) this.fitmentGidInput.value = "";
      if (this.button) this.button.disabled = true;
    }

    populateMakes() {
      const makes = this.normalizeSortUnique(this.fitmentIndex.map((x) => x.make));
      this.appendOptions(this.selectMake, makes);
      this.setDisabled(this.selectMake, false);
    }

    onMakeChange() {
      this.clearOptions(this.selectModel);
      this.clearOptions(this.selectYear);
      this.clearOptions(this.selectEngine);

      this.setDisabled(this.selectModel,  true);
      this.setDisabled(this.selectYear,   true);
      this.setDisabled(this.selectEngine, true);

      const make = this.getValue(this.selectMake);
      if (!make) {
        if (this.fitmentGidInput) this.fitmentGidInput.value = "";
        this.updateButtonAndAction();
        return;
      }

      const models = this.normalizeSortUnique(
        this.fitmentIndex.filter((x) => x.make === make).map((x) => x.model)
      );

      this.appendOptions(this.selectModel, models);
      this.setDisabled(this.selectModel, false);

      if (this.fitmentGidInput) this.fitmentGidInput.value = "";
      this.updateButtonAndAction();
    }

    onModelChange() {
      this.clearOptions(this.selectYear);
      this.clearOptions(this.selectEngine);

      this.setDisabled(this.selectYear,   true);
      this.setDisabled(this.selectEngine, true);

      const make  = this.getValue(this.selectMake);
      const model = this.getValue(this.selectModel);
      if (!make || !model) {
        if (this.fitmentGidInput) this.fitmentGidInput.value = "";
        this.updateButtonAndAction();
        return;
      }

      const years = this.normalizeSortUnique(
        this.fitmentIndex
          .filter((x) => x.make === make && x.model === model)
          .map((x) => x.year)
      );

      this.appendOptions(this.selectYear, years);
      this.setDisabled(this.selectYear, false);

      if (this.fitmentGidInput) this.fitmentGidInput.value = "";
      this.updateButtonAndAction();
    }

    onYearChange() {
      this.clearOptions(this.selectEngine);
      this.setDisabled(this.selectEngine, true);

      const make  = this.getValue(this.selectMake);
      const model = this.getValue(this.selectModel);
      const year  = this.getValue(this.selectYear);
      if (!make || !model || !year) {
        if (this.fitmentGidInput) this.fitmentGidInput.value = "";
        this.updateButtonAndAction();
        return;
      }

      const engines = this.normalizeSortUnique(
        this.fitmentIndex
          .filter((x) => x.make === make && x.model === model && x.year === year)
          .map((x) => x.engine)
      );

      this.appendOptions(this.selectEngine, engines);
      this.setDisabled(this.selectEngine, false);

      if (this.fitmentGidInput) this.fitmentGidInput.value = "";
      this.updateButtonAndAction();
    }

    onEngineChange() {
      const make   = this.getValue(this.selectMake);
      const model  = this.getValue(this.selectModel);
      const year   = this.getValue(this.selectYear);
      const engine = this.getValue(this.selectEngine);

      if (!make || !model || !year || !engine) {
        if (this.fitmentGidInput) this.fitmentGidInput.value = "";
        this.updateButtonAndAction();
        return;
      }

      const match = this.fitmentIndex.find(
        (x) => x.make === make && x.model === model && x.year === year && x.engine === engine
      );

      if (this.fitmentGidInput) this.fitmentGidInput.value = match ? match.gid : "";
      this.updateButtonAndAction();
    }

    buildFinalURL() {
      const base = this.getBaseCollectionURL();
      const url  = new URL(base, window.location.origin);

      const make   = this.getValue(this.selectMake);
      const model  = this.getValue(this.selectModel);
      const year   = this.getValue(this.selectYear);
      const engine = this.getValue(this.selectEngine);
      const gid    = this.fitmentGidInput?.value || "";

      if (!gid) return url.toString();

      if (make)   url.searchParams.set("make",   make.toLowerCase().replace(/\s+/g, "-"));
      if (model)  url.searchParams.set("model",  model.toLowerCase().replace(/\s+/g, "-"));
      if (year)   url.searchParams.set("year",   year);
      if (engine) url.searchParams.set("engine", engine.toLowerCase().replace(/[\s\/]+/g, "-"));

      url.searchParams.set("filter.p.m.custom.fitment", gid);

      return url.toString();
    }

    updateButtonAndAction() {
      const url = this.buildFinalURL();
      if (this.form) this.form.setAttribute("action", url);

      const hasGid = !!(
        this.fitmentGidInput &&
        this.fitmentGidInput.value &&
        this.fitmentGidInput.value.includes("gid://")
      );
      if (this.button) this.button.disabled = !hasGid;
    }

    restoreFromURL() {
      try {
        const current = new URL(window.location.href);
        const gid    = current.searchParams.get("filter.p.m.custom.fitment");
        const make   = current.searchParams.get("make");
        const model  = current.searchParams.get("model");
        const year   = current.searchParams.get("year");
        const engine = current.searchParams.get("engine");

        if (gid) {
          const match = this.fitmentIndex.find((x) => x.gid === gid);
          if (match) {
            this.selectMake.value = match.make;
            this.onMakeChange();
            this.selectModel.value = match.model;
            this.onModelChange();
            this.selectYear.value = match.year;
            this.onYearChange();
            this.selectEngine.value = match.engine;
            this.onEngineChange();
            return;
          }
        }

        if (make && model && year && engine) {
          const makeFmt   = make.replace(/-/g, " ");
          const modelFmt  = model.replace(/-/g, " ");
          const engineFmt = engine.replace(/-/g, " ");

          const match = this.fitmentIndex.find(
            (x) =>
              x.make.toLowerCase()   === makeFmt.toLowerCase() &&
              x.model.toLowerCase()  === modelFmt.toLowerCase() &&
              x.year                 === year &&
              x.engine.toLowerCase() === engineFmt.toLowerCase()
          );

          if (!match) return;

          this.selectMake.value = match.make;
          this.onMakeChange();
          this.selectModel.value = match.model;
          this.onModelChange();
          this.selectYear.value = match.year;
          this.onYearChange();
          this.selectEngine.value = match.engine;
          this.onEngineChange();
        }
      } catch (e) {
        // ignore
      }
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}